import React from 'react';
import { Box, GridItem, PageGrid, Stack } from '@ads-core/components';
import {
  ComponentRendering,
  HtmlElementRendering,
  Placeholder,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from '@liander/context';
import { isComponentRendering } from 'src/utils/typeGuards/isComponentRendering';
import { isParagraph } from 'src/utils/typeGuards/isParagraph';
import { isTextWithSideImage } from 'src/utils/typeGuards/isTextWithSideImage';
import { isImageGallery } from 'src/utils/typeGuards/isImageGallery';
import { useAdsContext } from '@ads-core/providers';
import { TableOfContent } from '@ads-core/components';
import {
  TableOfContentItem,
  TableOfContentsContainerProps,
} from 'components/TableOfContentsContainer';
import { enrichParagraph } from './utils/enrichParagraph';
import { enrichTextWithSideImage } from './utils/enrichTextWithSideImage';
import { enrichImageGallery } from './utils/enrichImageGallery';

export const TableOfContentsContainer = (props: TableOfContentsContainerProps) => {
  const { isEditor } = useSitecoreContext();

  const context = useAdsContext();
  const placeholders = 'table-of-contents-container-content';
  const [tableOfContent, setTableOfContent] = React.useState<Array<TableOfContentItem>>([]);
  const components = React.useMemo(
    () => props.rendering.placeholders?.[placeholders] ?? [],
    [props.rendering.placeholders]
  );
  const topOffset = context.topHeaderHeight ? context.topHeaderHeight + 16 : 0;

  const handleIntersection = React.useCallback(
    (anchorId: string) => {
      const currentActiveItem = tableOfContent.find((item) => item.isActive);

      if (currentActiveItem?.anchorId !== anchorId) {
        setTableOfContent((prev) =>
          prev.map((item) => ({ ...item, isActive: item.anchorId === anchorId }))
        );
      }
    },
    [tableOfContent]
  );

  const handleSetTableOfContent = React.useCallback(
    (item: TableOfContentItem | undefined) => {
      if (!item) return;

      const existingItem = tableOfContent.find(
        (contentItem) => contentItem.anchorId === item.anchorId
      );

      if (existingItem) return;

      setTableOfContent((prev) => [...prev, item]);
    },
    [tableOfContent]
  );

  // Maps all the components within the placeholder and enrich them with the additional fields.
  // This way we can set the table of content items and the intersecting state.
  const enrichedComponents: Array<ComponentRendering | HtmlElementRendering> = React.useMemo(
    () =>
      components
        .map((component): ComponentRendering | HtmlElementRendering | null => {
          if (!isComponentRendering(component)) return component;

          if (isParagraph(component)) {
            const enrichedComponent = enrichParagraph({
              component,
              handleIsIntersection: handleIntersection,
            });

            handleSetTableOfContent(enrichedComponent.additionalFields?.tableOfContentItem);

            return enrichedComponent;
          }

          if (isTextWithSideImage(component)) {
            const enrichedComponent = enrichTextWithSideImage({
              component,
              handleIsIntersection: handleIntersection,
            });

            handleSetTableOfContent(enrichedComponent.additionalFields?.tableOfContentItem);

            return enrichedComponent;
          }

          if (isImageGallery(component)) {
            const enrichedComponent = enrichImageGallery({
              component,
              handleIsIntersection: handleIntersection,
            });

            handleSetTableOfContent(enrichedComponent.additionalFields?.tableOfContentItem);

            return enrichedComponent;
          }

          return null;
        })
        .filter((component) => component !== null),
    [components, handleSetTableOfContent, handleIntersection]
  );

  return (
    <Box
      padding={isEditor ? 4 : 0}
      style={isEditor ? { border: '1px dotted black' } : undefined}
      position="relative"
      asChild
    >
      <PageGrid>
        <GridItem columnStart="1" columnEnd="-1">
          <Stack direction={{ initial: 'columnReverse', md: 'row' }} gap={20}>
            {tableOfContent.length > 0 ? (
              <Box zIndex="1" position="sticky" bottom="0px" asChild>
                <Stack isFullHeight isFullWidth>
                  <Box
                    position="sticky"
                    width={{ initial: '100%', md: '276px' }}
                    top={`${topOffset}px`}
                  >
                    <TableOfContent title="Inhoudsopgave" items={tableOfContent} />
                  </Box>
                </Stack>
              </Box>
            ) : null}

            <Box position="relative" zIndex="0" asChild>
              <GridItem columnStart={{ initial: '1', md: '4' }} columnEnd="-1">
                <Stack direction="column" isFullWidth gap={10}>
                  <Placeholder
                    name={placeholders}
                    rendering={{
                      ...props.rendering,
                      placeholders: {
                        ...props.rendering.placeholders,
                        [placeholders]: enrichedComponents,
                      },
                    }}
                  />
                </Stack>
              </GridItem>
            </Box>
          </Stack>
        </GridItem>
      </PageGrid>
    </Box>
  );
};

export default TableOfContentsContainer;
